// src/portal-app/tabs/HomeEodGlance.js
//
// Home's "previous business day" glance — the native counterpart of the web
// portal's EodGlanceWidget (components/portal/EodGlanceWidget.js). Same
// question answered ("what sold yesterday, and what did it eat from stock?"),
// rendered in the app's own idiom: kit CondensedRows inside a .tab-group,
// not the web widget's inline-styled card.
//
// Presentational only, per the kit convention stated in shell/kit/index.js —
// HomeTab owns the fetch and passes the payload down. `report` is the object
// from GET /end-of-day/report/last-closed, or null when the tenant has no
// closed business day yet.
import React from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { CondensedRow } from '../shell/kit';
import { useLanguage } from '../../context/LanguageContext';
import { formatQuantity } from '../../utils/units';

const TOP_PRODUCTS = 5;
const TOP_INGREDIENTS = 3;

const money = (value, currency) => {
    const rounded = Math.round((Number(value) || 0) * 100) / 100;
    return `${currency || ''}${Math.abs(rounded).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
};

// Local time, never toISOString(): a business day is a local calendar date.
// Parsing at midday sidesteps the DST edge that midnight parsing hits.
const businessDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const IconBox = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    </svg>
);

const IconStock = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 7h-9M14 17H5" />
        <circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" />
    </svg>
);

export default function HomeEodGlance({ report, loading, currency, onOpenEod }) {
    const { t } = useLanguage();

    const header = (
        <div className="hmt-sechead">
            <span className="pk-label">
                {t('previous_business_day', {}, 'Previous Business Day')}
            </span>
            {report?.sales_date && (
                <span className="pk-label pk-num">{businessDateLabel(report.sales_date)}</span>
            )}
        </div>
    );

    if (loading) {
        return (
            <>
                {header}
                <div className="tab-skel" style={{ height: 156 }} />
            </>
        );
    }

    // 204 from the endpoint — no closed business day yet. Not an error state.
    if (!report) {
        return (
            <>
                {header}
                <div className="tab-group">
                    <GlassCard veil className="tab-state">
                        <p className="tab-empty-msg">
                            {t('eod_no_closed_day', {}, 'No closed business day yet')}
                        </p>
                    </GlassCard>
                </div>
            </>
        );
    }

    const products = (report.products || []).slice(0, TOP_PRODUCTS);
    const stockWatch = ((report.ingredients || {}).usage || []).slice(0, TOP_INGREDIENTS);

    return (
        <>
            {header}
            <div className="tab-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {products.length === 0 ? (
                    <GlassCard veil className="tab-state">
                        <p className="tab-empty-msg">
                            {t('eod_no_products_sold', {}, 'Nothing was sold on this business day.')}
                        </p>
                    </GlassCard>
                ) : (
                    products.map((p) => (
                        <CondensedRow
                            key={p.product_code}
                            testId={`eod-product-${p.product_code}`}
                            icon={<IconBox />}
                            tone="blue"
                            name={p.product_name}
                            // Quantity is the headline — this whole section exists
                            // to answer "how many did we sell".
                            value={`×${p.quantity}`}
                            secondary={`${p.category} · ${money(p.revenue, currency)}`}
                        />
                    ))
                )}

                {/* Omitted entirely when the tenant has no inventory feature or
                    nothing moved — never an empty row. */}
                {stockWatch.length > 0 && (
                    <CondensedRow
                        testId="eod-stock-watch"
                        icon={<IconStock />}
                        tone="amber"
                        name={t('stock_watch', {}, 'Stock Watch')}
                        secondary={stockWatch
                            .map((i) => `${i.name} ${formatQuantity(i.quantity, i.unit)}`)
                            .join(' · ')}
                    />
                )}

                <CondensedRow
                    testId="eod-open-report"
                    icon={<IconBox />}
                    tone="teal"
                    name={t('view_full_eod_report', {}, 'Full end-of-day report')}
                    onPress={onOpenEod}
                />
            </div>
        </>
    );
}
